import _ from 'lodash';
import spec_schema from './schemas/api_spec.yaml';
import mime_schema from './schemas/mime_types.yaml';
import uuid from 'node-uuid';

import Validator from './validator';

const format_path = path => path.replace(/\/+/g, '/').replace(/\/$/, '');

const merge_policy = policies => _.mergeWith({}, ...policies, (objValue, srcValue, key, object, source, stack) => {
  // override entitlements instead of merging them
  if (key === 'entitlements') {
    return srcValue;
  }
  return undefined;
});

const process_method = (spec, ctx) => {
  const policy = spec.policy ? ctx.policy.concat(spec.policy) : ctx.policy;
  merge_policy(policy);
  const result = {
    spec,
    policy: merge_policy(policy),
    prefix: ctx.prefix,
    method: ctx.method,
    path: ctx.path
  };
  if (spec.request) {
    result.request = spec.request;
  }
  if (spec.response) {
    result.response = spec.response;
  }
  ctx.routes.push(result);
};

const process_methods = (spec, ctx) => {
  for (const [method, value] of Object.entries(spec)) {
    process_method(value, {...ctx, method});
  }
};

const process_prefix = (spec, ctx) => {
  const policy = spec.policy ? ctx.policy.concat(spec.policy) : ctx.policy;

  ctx.routes.push({
    spec,
    policy: merge_policy(policy),
    prefix: ctx.prefix,
    path: ctx.path
  });
};

const process_route = (spec, ctx) => {
  const policy = spec.policy ? ctx.policy.concat(spec.policy) : ctx.policy;

  for (const [key, value] of Object.entries(spec)) {
    if (key.startsWith('/')) {
      const prefix = key.endsWith('*');
      if (prefix && !value.methods) {
        process_prefix(value, {...ctx, prefix, path: ctx.path.concat(key), policy});
      }
      else {
        process_route(value, {...ctx, prefix, path: ctx.path.concat(key), policy});
      }
    }
    else if (key === 'methods') {
      process_methods(value, {...ctx, policy});
    }
  }
};

const default_policy = {
  security: {
    requirements: {
      authentication: false,
      entitlements: []
    }
  }
};

const process_spec = (spec, policy = {}) => {
  const ctx = {
    routes: [],
    path: [],
    policy: [policy, default_policy]
  };

  process_route(spec.routes, ctx);
  return ctx.routes;
};

const traverse = (node, fn) => {
  if (_.isObject(node)) {
    _.each(node, (val, key) => {
      fn(key, val);
      traverse(val, fn);
    });
  }
};

const spec_id_tag = Symbol();

// validator for API Spec
class SpecValidator extends Validator {
  constructor({schemas = [], ...opts} = {}) {
    super({
      ...opts,
      schemas: [spec_schema, mime_schema, ...schemas]
    });

    this._cache = {};
  }

  get_request_validator(spec, path, method) {
    const spec_id = spec[spec_id_tag];
    if (!spec_id) throw new Error('Must validate spec first');
    const key = `${spec_id}/requests${path.join('')}/${method}`;
    return this._cache[key];
  }

  get_response_validator(spec, path, method) {
    const spec_id = spec[spec_id_tag];
    if (!spec_id) throw new Error('Must validate spec first');
    const key = `${spec_id}/responses${path.join('')}/${method}`;
    return this._cache[key];
  }

  // need to validate every request/response schema in addition to the spec file structure itself
  async validate(spec, resolver) {
    if (!spec[spec_id_tag]) {
      spec[spec_id_tag] = uuid.v4();
    }
    const spec_id = spec[spec_id_tag];
    super.resolve_refs(spec);

    const routes = this.get_routes(spec);
    const schema_root = _.get(spec, 'info.schema_root', '');

    const request_schemas = routes
      .filter(route => _.get(route, 'request.body', null) !== null)
      .map(route => _.defaults({},
        _.isString(route.request.body) ? {$ref: `${schema_root}${route.request.body}`} : route.request.body,
        {id: `${spec_id}/requests${route.path.join('')}/${route.method}`})
      );

    for (const schema of request_schemas) {
      const compiled = await super.validate_schema(schema, resolver);
      if (!compiled) return false;
      this._cache[schema.id] = compiled;
    }

    const response_schemas = routes
      .filter(route => _.get(route, 'response.body', null) !== null)
      .map(route => _.defaults({},
        _.isString(route.response.body) ? {$ref: `${schema_root}${route.response.body}`} : route.response.body,
        {id: `${spec_id}/responses${route.path.join('')}/${route.method}`})
      );

    for (const schema of response_schemas) {
      const compiled = await super.validate_schema(schema, resolver);
      if (!compiled) return false;
      this._cache[schema.id] = compiled;
    }
    return super.validate(spec, resolver);
  }

  // return flattened route information with merged policies
  get_routes(spec, root_policy = {}) {
    return process_spec(spec, root_policy);
  }

  // return spec with only authenticated/entitled routes
  filter_entitlements(routes, authenticated = false, entitlements = []) {
    return routes.filter(route => {
      const security_requirements = route.policy.security.requirements;
      if (security_requirements.authenticated && !authenticated) {
        return true;
      }
      for (const entitlement of security_requirements.entitlements) {
        if (entitlements.indexOf(entitlement) === -1) {
          return true;
        }
      }
      return false;
    });
  }

  filter_spec(spec, root_policy, authenticated = false, entitlements = []) {
    const routes = this.get_routes(spec, root_policy);
    const filtered = this.filter_entitlements(routes, authenticated, entitlements);
    const cloned = _.cloneDeep(spec);

    for (const route of filtered) {
      // deletion path in document
      const path_to_remove = route.method ? ['routes', ...route.path, 'methods', route.method] : ['routes', ...route.path];
      const path_str = path_to_remove.join('/');

      // recursively delete route at path, and collapse on the way back up if no nested routes remain
      const delete_at = (node, remaining) => {
        // path should always exist in document
        if (node === undefined) {
          throw new Error(`Invalid deletion path: ${path_str}`);
        }

        // if this is the part we're deleting, return null
        if (remaining.length === 0) {
          return null;
        }

        // get next path component and result of recursive deletion
        const next = remaining.shift();
        const child = delete_at(node[next], remaining);

        if (child === null) {
          // if null, we want to delete the child
          delete node[next];
        }
        else if (_.isEmpty(child)) {
          // if empty, we want to delete the child
          delete node[next];
        }
        else if (next.startsWith('/')) {
          // if the child is a route, we delete it if it
          // has no sub-routes and is not a prefix
          if (child.methods || next.endsWith('*')) {
            node[next] = child;
          }
          else if (Object.keys(child).some(key => key.startsWith('/'))) {
            node[next] = child;
          }
          else {
            delete node[next];
          }
        }
        else {
          // default is to keep the recursive child intact
          node[next] = child;
        }
        return node;
      };

      delete_at(cloned, path_to_remove);
    }
    return cloned;
  }

  // retrieve specification tags
  get_tags(spec) {
    const tags = {};
    traverse(spec, (key, value) => {
      if (key === 'tags' && _.isArray(value)) {
        value.forEach(tag => tags[tag] = true);
      }
      if (key === 'methods') {
        Object.keys(value).forEach(key => {
          tags[key] = true;
        });
      }
    });
    return tags;
  }
}

export default SpecValidator;
